import {
    Component,
    effect,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    signal,
    WritableSignal
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { UtilsModule } from "@app/shared/modules/utils.module";
import { ContactService } from "@app/shared/services/contact.service";
import { StringUtils } from "@arch/_shared/utils/String.utils";
import { CompanyContactEntity } from "@arch/domain/entity";

@Component({
    selector: "app-share-list-select",
    imports: [SharedMaterialModule, SharedDsnModule, FormsModule, UtilsModule],
    templateUrl: "./share-list-select.component.html",
    styleUrl: "./share-list-select.component.scss"
})
export class ShareListSelectComponent implements OnInit {
    private readonly contactService = inject(ContactService);
    public user: CompanyContactEntity = this.contactService.get();
    @Input({ required: false })
    public disableActions: boolean = false;

    @Input({ required: false })
    public typeChips: "email" | "phone" = "phone";

    @Input({ required: false })
    public invalidMessageChips: string = "";

    @Input()
    public placeholderChips: string = "";

    @Input()
    public label: string = "";

    public textConfigs = {};

    @Input()
    public chips: string[] = [];

    @Input()
    public filterType: "whatsapp" | "email" = "email";
    @Input()
    public set categories(value: CategoriesShareListComponentType[]) {
        this._categories.set(value);
    }
    public get categories(): CategoriesShareListComponentType[] {
        return this._categories();
    }
    @Input() categoriesSignal: WritableSignal<
        CategoriesShareListComponentType[]
    >;

    public filteredCategories: CategoriesShareListComponentType[] = [];
    public selectAllChecked: boolean = false;
    public searchTerm: string = "";

    readonly _categories = signal<CategoriesShareListComponentType[]>([]);

    @Output()
    public handleContactDialog?: EventEmitter<CategoriesShareListComponentType> =
        new EventEmitter();

    @Output()
    public handleUpdateContact?: EventEmitter<ContactType> = new EventEmitter();

    constructor() {
        effect(() => {
            const categories = this.getModeledCategories(this._categories());

            this.setFilteredCategories(categories);
        });
    }

    ngOnInit(): void {
        this.categories = this.getModeledCategories(this.categories);
        this.filteredCategories = structuredClone(this.categories);
    }

    private getModeledCategories(
        categories: CategoriesShareListComponentType[]
    ): CategoriesShareListComponentType[] {
        return categories.map((category, index) => ({
            ...category,
            index,
            contacts: category.contacts
                .map((contact, contactIndex) => ({
                    ...contact,
                    index: contactIndex
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
        }));
    }

    onSelectAllChange(checked: boolean) {
        this.selectAllChecked = checked;

        this.categoriesSignal.update((categories) =>
            categories.map((category) => ({
                ...category,
                contacts: category.contacts.map((contact) => ({
                    ...contact,
                    ...(this.filterType === "whatsapp" && !contact?.contact
                        ? false
                        : { checked })
                }))
            }))
        );
    }

    updateSelectAllState(
        contact: ContactType,
        categoryId: string,
        contactId: string
    ) {
        this.categoriesSignal.update((categories) =>
            categories.map((category) => {
                if (category._id !== categoryId) return category;
                return {
                    ...category,
                    contacts: category.contacts
                        .map((c) => (c._id === contactId ? { ...contact } : c))
                        .sort((a, b) => a.name.localeCompare(b.name))
                };
            })
        );
        const updatedCategories = this.categoriesSignal();
        this.selectAllChecked = updatedCategories.every((category) =>
            category.contacts.every((contact) => contact.checked)
        );
    }

    public handleSearch(event: Event): void {
        const input = event.target as HTMLInputElement;
        const searchTerm = StringUtils.normalizeND(input.value);
        this.searchTerm = searchTerm;
        this.setFilteredCategories();
    }

    private setFilteredCategories(
        categories: CategoriesShareListComponentType[] = this.categoriesSignal()
    ) {
        this.filteredCategories = categories.map((category) => ({
            ...category,
            contacts: category.contacts
                .filter(
                    (contact) =>
                        StringUtils.normalizeND(contact?.name || "").includes(
                            this.searchTerm
                        ) ||
                        StringUtils.normalizeND(
                            contact?.contact || ""
                        ).includes(this.searchTerm)
                )
                .sort((a, b) => (a?.name).localeCompare(b?.name))
        }));
    }

    public onUpdateDisabledContact(contact: ContactType, type: string) {
        if (this.filterType !== "whatsapp") return;
        if (contact?.contact) return;
        if (contact?.contact || this.user.props?.role !== "Contabilidade")
            return;
        this.onUpdateContact(contact, type);
    }

    public onAddContact(category: CategoriesShareListComponentType): void {
        this.handleContactDialog.emit(category);
    }

    public onUpdateContact(contact: ContactType, type: string): void {
        contact.type = type;
        this.handleUpdateContact.emit(contact);
    }
}

export type CategoriesShareListComponentType = {
    _id: string;
    name: string;
    index?: number;
    contacts: ContactType[];
};

export type ContactType = {
    contact?: string;
    checked?: boolean;
    email?: string;
    name: string;
    _id: string;
    type?: string;
    index?: number;
};
